/**
 *  @file       Wallet API - perform operations related to Addresses, Keystores, Key Pairs
 *
 *  @author     Wes Garland    - wes@kingsds.network
 *  @author     Duncan Mays    - duncan@kingsds.network
 *  @author     Badrdine Sabhi - badr@kingsds.network
 *  @author     Ryan Rossiter  - ryan@kingsds.network
 *  @date       August 2019
 *  @module dcp/wallet
 *  @access public
 */
'use strict';

const debugging = require('dcp/debugging').scope('wallet');
exports.promptCreatePassphrase = false; /* override to use in Keystore */

const dcpEnv  = require('dcp/common/dcp-env');
const fsBasic = require('dcp/common/fs-basic');
const utils   = require('dcp/utils');

exports._internalEth = require('./keystore')._internalEth;  /* MUST load before sub-modules for ethereum module detection */

const ksCache = require('./ks-cache');
Object.assign(exports, require('./keystore'));
Object.assign(exports, require('./eth'));
Object.assign(exports, require('./address-collection'));
Object.assign(exports, require('./passphrase-prompt'));
Object.assign(exports, require('./error-codes'));

function isArrayLike(a)
{
  return typeof a === 'object' && (Array.isArray(a) || typeof a.length === 'number');
}

/**
 *  This property sets the name of the namespace that is used to generate the application data directory, which is used to store proxy keys when required,
 *  and users’ bank and identity keys when supplied.
 *  The default namespace name is the name specified in the package.json file for the application calling into this API.
 */
exports.namespaceName = undefined;

/**
 *  This property gets/sets the full path name the application data directory, overriding namespaceName if it is changed.
 *  The default value is ~/.dcp/applications/${namespaceName}/, where ~ represents the invoking user’s home directory.
 */
exports.stateDir = undefined;

/**
 * @access public
 * @typedef {object} LoadResult - Object returned by `wallet.load`
 * @property {Keystore} keystore - the instance of the keystore that was loaded
 * @property {boolean} safe - indicates that the keystore was read from a secure location.
 */


/**
 * Form 1
 * 
 * This function locates and reads a keystore file.
 * 
 * **NodeJS** only: `filename` must be an absolute path, or begin withgin with `path.sep, '.' + path.sep`, or `'..' + path.sep`.
 * The safe flag is set to true to indicate that the Keystore file was stored safely; this happens if and only if the following conditions are true:
 * 
 * 1. The containing directory and all its ancestors, up to and including the root directory, is not world-writable.
 * 2. The Keystore File is neither world-writable nor world-readable.
 * 
 * @param {string} filename The keystore filename
 * @returns Object with the following properties:
 *  - contents: the contents of the file
 *  - safe:     if the file was loaded from a safe path (perms check)
 *  - filename: the name of the file that was actually loaded
 */
/**
 * Form 2
 * 
 * This function locates and reads a keystore file.
 *
 * @param {object} options - An options object
 * @param {string} [options.name='default'] - The keystore label, or filename
 * @param {string[]} [options.paths] - Override default keystore directory search path, normally the application data directory, followed by the `.dcp` 
 * directory in the home directory belonging to the user invoking the program (NodeJS only). Note that the search path is never used when name specifies a 
 * complete pathname.
 * @param {string} [options.dir] - Overrides paths, equivalent to `paths: [dir]`
 * @returns Object with the following properties:
 *  - contents: the contents of the file
 *  - safe:     if the file was loaded from a safe path (perms check)
 *  - filename: the name of the file that was actually loaded
 */
function loadKeystore(options) {
  if (typeof options === 'string') {
    options = {
      name: utils.expandPath(options),
    };
  }

  /* Deep clone and use default values to augment the passed-in options object.
   * The deep clone is necessary because this API mutates properties of the object.
   */
  options = JSON.parse(JSON.stringify(Object.assign({}, {
    name: "default",
    paths: [ require('dcp/utils/resource-dir').getResourceDir() ],
  }, options)));

  let filename;
  if (options.name.match(/[/\\]/)) {
    // if the name contains a slash (or backslash), then treat it as a filename
    filename = utils.expandPath(options.name);

    if (!fsBasic.fileExists(filename)) {
      let err = new Error(`Could not locate keystore '${filename}'`);
      err.code = 'ENOENT';
      throw err;
    }
  } else {
    // otherwise, treat the name as a keystore label and search
    // for a keystore named `${options.name}.keystore` in options.paths

    if (options.dir)
      options.paths = [utils.expandPath(options.dir)];
    else if (exports.stateDir)
      options.paths.unshift(fsBasic.pathResolve(exports.stateDir))
    else if (exports.namespaceName)
      options.paths.unshift(fsBasic.pathResolve(utils.expandPath('~/.dcp/applications'), exports.namespaceName));
    
    let searchPaths = options.paths.map((searchPath) =>
      fsBasic.pathResolve(searchPath, `${options.name}.keystore`)
    );

    filename = searchPaths.find((fn) => {
      debugging('search') && console.log(`wallet - checking ${fn}`);
      return fsBasic.fileExists(fn);
    });

    if (filename) {
      debugging('searchResult') && console.log(`wallet - found keystore at ${filename}`);
    } else {
      const errMsg = `Could not locate keystore named '${options.name}' in these locations: ${JSON.stringify(searchPaths)}`;
      let err = new Error(errMsg);
      debugging('searchResult') && console.error(`wallet - ${errMsg}`);
      err.code = 'ENOENT';
      throw err;
    }
  }

  debugging() && console.log(`wallet - loading '${filename}'`);
  //gets the keystore or private key from the file
  return {
    safe: exports.isSafe(filename),
    filename: filename,
    contents: fsBasic.readFile(filename).trim(),
  };
}

/**
 * Form 1
 * 
 * This function locates and reads a keystore file,
 * and instantiates a Keystore from it.
 * 
 * **NodeJS** only: `filename` must be an absolute path, or begin withgin with `path.sep, '.' + path.sep`, or `'..' + path.sep`.
 * The safe flag is set to true to indicate that the Keystore file was stored safely; this happens if and only if the following conditions are true:
 * 
 * 1. The containing directory and all its ancestors, up to and including the root directory, is not world-writable.
 * 2. The Keystore File is neither world-writable nor world-readable.
 * 
 * @param {string} filename The keystore filename
 * @returns {module:dcp/wallet~LoadResult}
 * @async
 * @access public
 */
/**
 * Form 2
 * 
 * This function locates and reads a keystore file,
 * and instantiates a Keystore from it.
 *
 * @param {object} options - An options object
 * @param {string} [options.name='default'] - The keystore label, or filename
 * @param {string[]} [options.paths] - Override default keystore directory search path, normally the application data directory, followed by the `.dcp` 
 * directory in the home directory belonging to the user invoking the program (NodeJS only). Note that the search path is never used when name specifies a 
 * complete pathname.
 * @param {string} [options.dir] - Overrides paths, equivalent to `paths: [dir]`
 * @param {KeystoreConstructor} [options.KeystoreConstructor] - Constructor to use when creating the keystore
 * @param {boolean} [options.checkEmpty] - if empty password should be tried before prompting the user.
 * @returns {module:dcp/wallet~LoadResult}
 * @async
 * @access public
 */
exports.load = async function wallet$$load(options) {
  var { contents, filename, safe } = loadKeystore.apply(this, arguments);
  var KeystoreConstructor = (options && options.KeystoreConstructor) || exports.Keystore;
  const keystore = await new KeystoreConstructor(JSON.parse(contents), false, options.checkEmpty);
  if (!keystore.label)
    keystore.label = fsBasic.basename(filename.replace(/\.keystore$/i, ''));
  
  //return keystore in an object with a safety indicator
  return {
    keystore,
    filename,
    safe
  }
}

/**
 * Form 1
 * 
 * This function locates and reads a keystore file,
 * and instantiates an Address from it.
 * 
 * **NodeJS** only: `filename` must be an absolute path, or begin withgin with `path.sep, '.' + path.sep`, or `'..' + path.sep`.
 * The safe flag is set to true to indicate that the Keystore file was stored safely; this happens if and only if the following conditions are true:
 * 
 * 1. The containing directory and all its ancestors, up to and including the root directory, is not world-writable.
 * 2. The Keystore File is neither world-writable nor world-readable.
 * 
 * @param {string} filename The keystore filename
 * @access public
 */
/**
 * Form 2
 * 
 * This function locates and reads a keystore file,
 * and instantiates an Address from it.
 *
 * @param {object} options - An options object
 * @param {string} [options.name='default'] - The keystore label, or filename
 * @param {string[]} [options.paths] - Override default keystore directory search path, normally the application data directory, followed by the `.dcp` 
 * directory in the home directory belonging to the user invoking the program (NodeJS only). Note that the search path is never used when name specifies a 
 * complete pathname.
 * @param {string} [options.dir] - Overrides paths, equivalent to `paths: [dir]`
 * @access public
 */
exports.loadAddress = function wallet$$loadAddress(options) {
  var { contents, filename, safe } = loadKeystore.apply(this, arguments);

  return {
    filename,
    address: new exports.Address(JSON.parse(contents).address),
    safe
  };
}

/**
 *  @param {Object} keystore             instance of Keystore to save
 *  @param {string} filename             the filename to save it in
 */
exports.save = async function wallet$$save(keystore, filename)
{
  const dir = fsBasic.dirname(filename);
  if (!fsBasic.dirExists(dir))
    fsBasic.mkdir(dir);
  fsBasic.writeFile(filename, JSON.stringify(keystore));
}

/**
 * This function checks the safety requirements outlined in the Keystore API documentation
 * The requirements are that the file must neither be world readable or world writable, 
 * or have any parent directory that is world writable. This function will return a boolean which indicates
 * weather or not these conditions are met.
 *
 * @param   {string}  - filePath, this is the path to the file we are checking the safety of, this
 *                      function will not work properly, and in fact will log a warning message, if that filePath 
 *                      starts with either . or ..
 * @returns {boolean} - indicates weather or not the file provided satisfies safety criteria
 */
exports.isSafe = function wallet$$isSafe(filePath)
{
  if (!fsBasic.absPath(filePath))
    throw new Error(`Not an absolute path: '${filePath}'`);

  if (exports.isWorldReadable(filePath))
    return false;

  /** @bug fragile testing below disabled until win32 and pythonmonkey compatibility can be looked at. /wg feb 2024 */
  return true;

  //checks that the file and all its ancestor directories are not world readable
  let pathArray = filePath.split(path.sep);
  pathArray[0] = pathArray[0] || path.parse(filePath).root;
  while (pathArray.length > 0) {
    let reassemble = fsBasic.joinPath(...pathArray);
    if (exports.isWorldWritable(reassemble))
      return false;
    pathArray = pathArray.slice(0, pathArray.length - 1);
  }

  return true;
}

/**
 * This function takes a filepath and returns a boolean to indicate weather or not the location specified in the filepath is world readable
 *
 * @param   {string}  filePath  - path to the file in question, does not work right if the filePath starts with . or ..
 *
 * @returns {boolean}           - indicates weather the given file or directory is world readable
 */
exports.isWorldReadable = function wallet$$isWorldReadable(filePath) {
  const mode = fsBasic.getMode(filePath);
  return mode & fsBasic.constants.R_OK;
}

/**
 * This function takes a filepath and returns a boolean to indicate weather or not the location specified in the filepath is world writable
 *
 * @param   {string}  filePath - path to the file in question, does not work right if the filePath starts with . or ..
 *
 * @returns {boolean}          - indicates weather the given file or directory is world writable.
 */
exports.isWorldWritable = function wallet$$isWorldWritable(filePath) {
  debugging('safety') && console.debug(`wallet - checking write permissions on '${filePath}'`);
  const mode = fsBasic.getMode(filePath);
  return mode & fsBasic.constants.W_OK;
}

function walletUsesOAuth(options)
{
  if (options.hasOwnProperty('oAuth'))
    return Boolean(options.oAuth);
  if (dcpConfig.oAuth?.walletIntegration === true)
    return true;
  if (dcpEnv.getenv('dcp-wallet-oauth-integration'))
    return true;
}

/**
 * These functions examine parameters and accepts options relating to its environment, uses this information 
 * to select a Keystore File, load it via wallet.load(), and return a Keystore object.
 * If it cannot return a Keystore object, this function will throw an Error.
 *
 * @param   {object | string} param1 | param2 - Three accepted keys: name, which specifies the name of the keystore, dir, which specifies its directpry, 
 *                                              and checkEmpty, which controls weather or not unlock() will attempt an empty password on the keystore 
 *                                              before prompting the user.
 * @returns {Object} instance of Keystore
 */
async function wallet$$get_browser(options) {
  const ClientModal = require('dcp/dcp-client/dom-tk').modals;
  var keystoreFileContents;
  
  keystoreFileContents = await exports.getKeystoreFromStorage(`dcp-keystore-${options.name}-${options.contextId}`, options.jobName);
  if (keystoreFileContents)
    return new options.KeystoreConstructor(keystoreFileContents);

  if (walletUsesOAuth(options))
  {
    // Don't we have an entirely different oauth flow that should be used with login stuff for this? I'm basing
    // this off the old oauthLogin modal stuff, but vaguely recalling we had ways to do this that didn't involve popout windows
    const loginDesired = await ClientModal.confirm('Log in to the DCP Portal to access your wallet.', );
    if (!loginDesired)
      throw new Error('Unable to retrieve keystore - will not log in');

    keystoreFileContents = await new Promise((resolve, _reject) => {
      window.open(dcpConfig.oAuth.location.resolve('/reflector'), 'DCP Login', 'popup=yes,left=200,top=200,width=400,height=360');
      // wait for a message back from the popup with the bank account keystore
      window.addEventListener('message', function anonGetKeystoreFromPopup$$handleMessage(event) {
        // make sure it is coming from the reflector window
        if (event.origin && dcpConfig.oAuth.location.sameOrigin(event.origin)) 
        {
          window.removeEventListener('message', anonGetKeystoreFromPopup$$handleMessage);
          resolve(event.data);
        }
      });
    })
  }
  else
  {
    keystoreFileContents = await ClientModal.uploadKeystore(options);
  }

  return new options.KeystoreConstructor(keystoreFileContents);
}

async function wallet$$get_node(options)
{
  async function inner()
  {
    const result = await exports.load(options);

    if (result && result.keystore)
      return result.keystore;
    return false;
  }

  return inner();
}

/**
 * Get a keystore from the local filesystem
 */
async function wallet$$get_fs(options)
{
  const result = await exports.load(options);
  if (result && result.keystore)
    return result.keystore;
  return false;
}

const getKeystore_platform = dcpEnv.switch({
  pythonmonkey:  wallet$$get_fs,
  nodejs:        wallet$$get_node,
  bravojs:       wallet$$get_browser,
  'vanilla-web': wallet$$get_browser,
  default:       () => { throw new Error(`unknown platform ${dcpEnv.platform}; cannot load keystore`) },
});

/** 
 * Return a promise which resolves to an identity keystore. See {@link module:dcp/wallet.get|get}.
 *
 * If an identity keystore has never been generated or added to the wallet with addId, a new
 * one will be either fetched if there are NO arguments passed to this function.
 *
 * On browser platforms, an idKs will be dynamically generated if one has not been specified
 * beforehand. This behaviour can be defeated by passing arguments=[false], which will be treated 
 * the same way as arguments=[].
 *
 * On browser platforms, we try to "fetch" the identity from a file upload, but ONLY if the
 * arguments to this function have been specified. The idea here is that the default behaviour,
 * for getId() to generate a random id, is the best behaviour from a UX POV unless the developer 
 * is doing something really weird.
 *
 * On non-browser platforms, we "fetch" the identity via wallet.get(), which eventually falls
 * back to looking in ~/.dcp/id.keystore.
 *
 * Implementation note: cannot await between function call and initial population of ksCache in 
 * walletGet_form1 if adding a new identity; otherwise, multiple parallel connections might 
 * trigger new identity "fetches" etc.
 * 
 * @returns {module:dcp/wallet.IdKeystore}
 * @access public
 * @async
 */
exports.getId = async function wallet$$getId()
{
  var optionsOrNameOrNada;
  var ks;
  var options = {
    name: 'id',
    KeystoreConstructor: exports.IdKeystore
  };
  var generateKeystoreOnDemand = true;
  
  if (arguments.length === 1 && arguments[0] === false)
  {
    generateKeystoreOnDemand = false;
    delete arguments[0];
  }

  if (!isArrayLike(arguments[0])) /* forms 4,5,6 */
    optionsOrNameOrNada = arguments[0];
  else
  {
    Object.assign(options, walletGet_argsToOptions(exports.IdKeystore, arguments[0]));
    optionsOrNameOrNada = arguments[1]; /* re-write forms 4/5/6 to 2/1/3 */
  }

  if (typeof optionsOrNameOrNada === 'object')
    Object.assign(options, optionsOrNameOrNada);                /* form 1 - options */
  else if (typeof optionsOrNameOrNada === 'undefined')
    ;                                                           /* form 2 - name */
  else if (typeof optionsOrNameOrNada === 'string')
    options.name = optionsOrNameOrNada;                         /* form 3 - nada */
  else
    throw new Error('unrecognized constructor form in wallet::getId');

  if (dcpEnv.isBrowserPlatform && generateKeystoreOnDemand && arguments.length === 0)
  {
    options.fetch = function wallet$$getId$generate() {
      debugging() && console.debug('wallet - generating dynamic identity');
      return new exports.IdKeystore(null, '');      
    };
  }

  let cached = await ksCache.get(options.KeystoreConstructor, options.name, options.contextId);
  if (cached)
    return cached;

  ks = await walletGet_form1(options);

  debugging('identity') && console.debug('wallet - identity is', !ks ? ks : ks.address);
  return ks;
}

/** 
 * Form 1
 * 
 * This form loads a Keystore File via {@link module:dcp/wallet.load|load}.
 * The options object is used to override default parameters which help to identify and locate the Keystore File.
 * 
 * **On NodeJS**: The options object is passed directly to `wallet.load`.
 * 
 * **On Web**: The options object is primarily used to provide context to the user for showing a modal; 
 * @param {object} options
 * @param {string} [options.name='default'] Override the default keystore name.
 * @param {string} [options.contextId] An optional, user-defined identifier used for caching keystores.
 *                                     See `job.contextId` in the compute-api spec.
 * @param {string} [options.jobName] Optional name of the job that the keystore is being requested for.
 * @param {boolean} [options.checkEmpty=true] Try an empty password before prompting user
 * @param {function} [options.KeystoreConstructor] Override the constructor to use for the keystore.
 * @param {string} [options.msg] Custom message for that modal that describes the purpose for providing the keystore
 * @return {module:dcp/wallet.Keystore} Return a keystore.
 * @async
 * @access public
 *
 * **On All Platforms**: The option object has these properties
 *  - oAuth: true or false to enable or disable oAuth integration. Currently, oAuth integration is
 *           disabled by default, but it will eventually become enabled by default; probably by
 *           summer 2023.
 */
/**
 * Form 2
 * 
 * This form is equivalent to `wallet.get({})`.
 * @return {module:dcp/wallet.Keystore} Return a keystore.
 * @async
 * @access public
 */
/**
 * Form 3
 * 
 * This form is equivalent to `wallet.get({name: string})`
 * @param {string} name Override the default keystore name.
 * @return {module:dcp/wallet.Keystore} Return a keystore.
 * @async
 * @access public
 */
/**
 * Form 4
 * 
 * This form accepts an Array-like object, formatted like `process.argv`, to specify options for invoking Form 1. 
 * Its purpose is to unify Keystore, PrivateKey, and Address-related options for programs written in NodeJS.
 * @param {string[]} array Array-like object, formatted like `process.argv`
 * | Argument      | Behaviour |
 * |---------------|-----------|
 * | --private-key | Generate a keystore corresponding to the specified private key, ignoring all other considerations |
 * | -i name       | Override the `name` option for getId |
 * | -k name       | Override the `name` option for get |
 * | -p            | Set the `checkEmpty` option to false |
 * @return {module:dcp/wallet.Keystore} Return a keystore.
 * @async
 * @access public
 */
/**
 * Form 5
 * 
 * This form accepts an Array-like object, as described in form 4, and an options object 
 * as described in form 1. Its behaviour is to generate an options object from the array, 
 * merge the passed options object into this options object, and invoke form 1.
 * To be clear, the options argument has a higher precedence than the array argument.
 * @param {string[]} array Array-like object, formatted like `process.argv`. (See Form 4)
 * @param {object} options Options object, see Form 1.
 * @return {module:dcp/wallet.Keystore} Return a keystore.
 * @async
 * @access public
 */
/**
 * Form 6
 * 
 * This form is equivalent to `wallet.get(array, {name: string})`.
 * @param {string[]} array Array-like object, formatted like `process.argv`. (See Form 4)
 * @param {string} name Override the default keystore name.
 * @return {module:dcp/wallet.Keystore} Return a keystore.
 * @async
 * @access public
 */
exports.get = async function wallet$$get()
{
  var optionsOrNameOrNada;
  var ks;
  var options = {
    name: 'default',
    KeystoreConstructor: exports.BankAccountKeystore
  };

  if (!isArrayLike(arguments[0])) /* forms 4,5,6 */
    optionsOrNameOrNada = arguments[0];
  else
  {
    Object.assign(options, walletGet_argsToOptions(exports.BankAccountKeystore, arguments[0]));
    optionsOrNameOrNada = arguments[1]; /* re-write forms 4/5/6 to 2/1/3 */
  }

  if (options.privateKey && typeof optionsOrNameOrNada === 'undefined') /* for 4 with private key - generate keystore with private key ignoring all other considerations */
    return new exports.BankAccountKeystore(options.privateKey, false)
  else if (typeof optionsOrNameOrNada === 'object')
  {
    if (Object.keys(optionsOrNameOrNada).length !== 0)
      Object.assign(options, optionsOrNameOrNada); /* form 1 - options */                
  }
  else if (typeof optionsOrNameOrNada === 'undefined')
    ;                                                           /* form 2 - name */
  else if (typeof optionsOrNameOrNada === 'string')
    options.name = optionsOrNameOrNada;                         /* form 3 - nada */
  else
    throw new Error('unrecognized constructor form in wallet::get');
  
  return walletGet_form1(options);
}

/**
 * @returns Promise<Keystore>
 */
async function walletGet_form1(options)
{
  var ksPromise;
  
  if (ksCache.has(options.name, options.contextId))
    return ksCache.get(options.KeystoreConstructor, options.name, options.contextId);

  if (options.fetch)
    ksPromise = options.fetch(options);
  else
    ksPromise = await getKeystore_platform(options);
  ksCache.set(options.KeystoreConstructor, options.name, options.contextId, ksPromise);
  
  return ksPromise;
}

function walletGet_argsToOptions(ksCtor, argv)
{
  var options = {};

  for (let i=0; i < argv.length; i++)
  {
    if (argv[i][0] !== '-')
      continue;
    
    switch(argv[i]) {
    case '--private-key':
      options.privateKey = argv[++i];
      break;
    case '-p':
      argv.splice(i--, 1); 
      options.checkEmpty = false;
      break;
    case '-i':
      if (ksCtor === exports.IdKeystore)
        options.name = argv[++i];
      break;
    case '-k':
      if (ksCtor === exports.BankAccountKeystore)
        options.name = argv[++i];
      break;
    }
  }
  return options;
}

/** Keystore can be a keystore or a promise that resolves to a Keystore */
function add_internal(keystore, options)
{
  ksCache.set(options.KeystoreConstructor, options.name, options.contextId, keystore);
}

/** Add a temporary Keystore (in RAM) to the current wallet, which can be retrieved later by {@link module:dcp/wallet.get|get}.
 *  @param {module:dcp/wallet.Keystore} keystore The keystore to add
 *  @param {string} [name='default'] The name of the keystore
 *  @param {string} [contextId] An optional identifier for caching keystores. See `job.contextId` in the compute-api spec.
 * @access public
 */
/** Add a temporary Keystore (in RAM) to the current wallet, which can be retrieved later by {@link module:dcp/wallet.get|get}.
 *  @param {Promise} keystore A promise which resolves to the keystore to add
 *  @param {string} [name='default'] The name of the keystore
 *  @param {string} [contextId] An optional identifier for caching keystores. See `job.contextId` in the compute-api spec.
 * @access public
 */
/** Add a temporary Keystore (in RAM) to the current wallet, which can be retrieved later by {@link module:dcp/wallet.get|get}.
 *  @param {DcpURL|URL} keystore A URL which can be used to fetch to the keystore to add
 *  @param {string} [name='default'] The name of the keystore
 *  @param {string} [contextId] An optional identifier for caching keystores. See `job.contextId` in the compute-api spec.
 * @access public
 */
exports.add = function wallet$$add(keystore, name='default', contextId, KeystoreConstructor=exports.Keystore)
{
  return add_internal(keystore, { name, contextId, KeystoreConstructor });
}

/** 
 * Same as {@link module:dcp/wallet.add|add}, except for retrieval by {@link module:dcp/wallet.getId|getId}
 *  @param {module:dcp/wallet.Keystore} keystore The keystore to add
 *  @param {string} [name='default'] The name of the keystore
 *  @param {string} [contextId] An optional identifier for caching keystores. See `job.contextId` in the compute-api spec.
 * @access public
 */    
exports.addId = function wallet$$addId(keystore, name='id', contextId, KeystoreConstructor=exports.IdKeystore)
{
  return add_internal(keystore, { name, contextId, KeystoreConstructor });
}

/**
 * Removes all keystore entries from the cache.
 * @access public
 */
exports.clear = function wallet$$clear() {
  ksCache.reset();
}

exports.remove = function wallet$$remove(name='default', contextId)
{
  ksCache.remove(name, contextId)
}

exports.export = function wallet$$export()
{
  return ksCache.export();
}

exports.import = function wallet$$import()
{
  return ksCache.import();
}

exports.version = {
  api: '1.2.4',
  provides: '1.0.0'
};
